import { format } from 'date-fns';

import { LineItem } from '../validationSchemas/LineItemSchema';
import { removeCommas, removeCommasAndOmitCents } from './helpers';
import { snakeCaseKeys } from './object';

const mapFormToLineItem = (data: LineItem) => ({
  ...snakeCaseKeys(data),
  // required fields
  id: data.lineItemId || null,
  name: data.lineItemName,
  start_date: data.startDate ? format(data.startDate, 'yyyy-MM-dd') : null,
  end_date: undefined,
  final_end_date: data.endDate ? format(data.endDate, 'yyyy-MM-dd') : null,
  advisory_budget: removeCommas(data.advisoryBudget || (data as any).advisory_budget),
  // Omitted with profiles — delivery job derives pacing per-flight.
  impression_budget: (data as any).customDeliveryProfiles?.length > 0
    ? undefined
    : removeCommasAndOmitCents(data.impressionBudget),
  // Always sent so users can grow the cap; schema enforces floor (ADOPS-2639).
  bonus: removeCommasAndOmitCents(data.bonus),
  pricing_rate: removeCommasAndOmitCents(Number(data.pricingRate) * 100),
  min_ctr_optimization: data.minCtrOptimization || null,
  min_conversion_optimization: data.minConversionOptimization || null,

  // conditionally included fields
  frequency_capping: data.frequencyCapEnabled ? data.frequencyCapping : null,
  max_user_views_per_period: data.frequencyCapEnabled ? data.maxUserViewsPerPeriod : null,
  click_frequency_capping: data.performanceCapEnabled ? data.clickFrequencyCapping : null,
  max_user_clicks_per_period: data.performanceCapEnabled ? data.maxUserClicksPerPeriod : null,

  // Do not pass this in and let the server calculate it
  dollar_budget: undefined,
  delivery_status: data.deliveryStatus || null,
  pid_type: data.pidType || null,
  kpi_goal: data.kpiGoal && JSON.stringify(data.kpiGoal),
  seed_type_ids: undefined,
  // kpiObjectives is only used to keep the state of the kpiGoal for the form.
  kpi_objectives: undefined,
  visit_store_list: {
    ...snakeCaseKeys(data.visitStoreList),
    store_list: data.visitStoreList?.storeList?.map((item) => (item as any).label || item).join(','),
  },
  receipt_product_list: {
    ...snakeCaseKeys(data.receiptProductList),
    product_list: data.receiptProductList?.productList?.map((item) => (item as any).label || item).join(','),
    product_lower_categories: data.receiptProductList?.productLowerCategories?.map((item) => (item as any).label || item).join(','),
  },

  // Nested delivery profiles (Rails accepts_nested_attributes_for)
  delivery_profiles: [
    ...((data as any).customDeliveryProfiles || []).map((p: any) => ({
      id: p.id && typeof p.id === 'number' ? p.id : undefined,
      start_date: p.startDate ? format(p.startDate, 'yyyy-MM-dd') : null,
      end_date: p.endDate ? format(p.endDate, 'yyyy-MM-dd') : null,
      profile_bonus: p.bonus,
      profile_dollar_budget: p.dollarBudget,
      delivered_impressions: p.deliveredImpressions,
      profile_impression_budget: p.profileImpressionBudget,
      is_active: p.active,
      disabled: p.disabled,
    })),
    ...((data as any).deletedProfileIds || []).map((id: number) => ({
      id,
      _destroy: true,
    })),
  ],

  // Prevent snakeCaseKeys from leaking form-only fields
  custom_delivery_profiles: undefined,
  deleted_profile_ids: undefined,
});

export default mapFormToLineItem;
